/**
 * cart-recommendations.js
 * Unified recommendation client for the Brewout cart.
 *
 * Primary path: /apps/reco/cart endpoint (app proxy)
 * Fallback path: Shopify /recommendations/products endpoint
 *
 * Features:
 *   – Result caching with cart-fingerprint invalidation
 *   – 400ms debounce on cart-change events
 *   – Full persona + session_id params
 *   – Renders Section Rendering API HTML or falls back to JSON rendering
 *   – Telemetry integration via CartTelemetry
 */
(function () {
  'use strict';

  /* ── Cache ──────────────────────────────────────────────────────────────────── */
  const CACHE_KEY = 'cu_reco_cache';
  const CACHE_TTL_MS = 3 * 60 * 1000; // 3 min

  const getCache = () => {
    try {
      return JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
    } catch { return null; }
  };
  const setCache = (fingerprint, data) => {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({
        fingerprint,
        data,
        ts: Date.now(),
      }));
    } catch {/* quota */}
  };
  const isCacheValid = (cached, fingerprint) => {
    if (!cached) return false;
    if (cached.fingerprint !== fingerprint) return false;
    if (Date.now() - cached.ts > CACHE_TTL_MS) return false;
    return true;
  };

  const makeFingerprint = (cart) => {
    return (cart.items || []).map(i => `${i.product_id}:${i.quantity}`).join(',')
      + '|' + (cart.total_price || 0);
  };

  /* ── Debounce ────────────────────────────────────────────────────────────────── */
  let _debounceTimer = null;
  const debounce = (fn, ms) => {
    clearTimeout(_debounceTimer);
    _debounceTimer = setTimeout(fn, ms);
  };

  /* ── Money formatter ─────────────────────────────────────────────────────────── */
  const money = (cents) => {
    if (window.CartCore) return window.CartCore.money(cents);
    return new Intl.NumberFormat(navigator.language, {
      style: 'currency', currency: 'EUR',
    }).format(cents / 100);
  };

  /* ── Persona params ──────────────────────────────────────────────────────────── */
  const buildPersonaParams = (params) => {
    const p = window.CartCore
      ? window.CartCore.getPersona()
      : { persona: localStorage.getItem('brewout_persona') || '',
          confidence: parseFloat(localStorage.getItem('brewout_persona_confidence') || '0'),
          scores: localStorage.getItem('brewout_persona_scores') || '',
          sessionId: localStorage.getItem('brewout_session_id') || '' };

    if (p.persona && p.confidence >= 0.5) {
      params.set('persona', p.persona);
      params.set('persona_confidence', String(p.confidence));
    }
    if (p.scores) params.set('persona_scores', p.scores);
    if (p.sessionId) params.set('session_id', p.sessionId);
  };

  /* ── App proxy fetch ────────────────────────────────────────────────────────── */
  const fetchFromProxy = async (cart, limit) => {
    const cartItems = cart.items || [];
    const cartProductIds = cartItems.map(i => String(i.product_id));
    const minItemPriceCents = cartItems.length > 0
      ? Math.min(...cartItems.map(i => i.price))
      : 0;

    const params = new URLSearchParams({
      product_ids: cartProductIds.join(','),
      cart_total_cents: String(cart.total_price || 0),
      min_item_price_cents: String(minItemPriceCents),
      limit: String(limit + 2), // over-fetch to allow deduplication
    });
    buildPersonaParams(params);

    const resp = await fetch(`/apps/reco/cart?${params}`, {
      credentials: 'same-origin',
    });
    if (!resp.ok) throw new Error(`reco proxy ${resp.status}`);
    const data = await resp.json();

    return (data.items || [])
      .filter(r => !cartProductIds.includes(String(r.product_id)))
      .slice(0, limit)
      .map(r => ({
        product_id: String(r.product_id),
        handle: r.handle,
        title: r.product_name || r.title || '',
        price: r.price_cents || 0,
        image: r.image || '',
        variant_id: r.variant_id,
        algo: r.algo || r.relation_type || '',
        reason: r.reason || '',
      }));
  };

  /* ── Shopify recommendations fallback ────────────────────────────────────────── */
  const fetchFromShopify = async (cart, limit, productId) => {
    const params = new URLSearchParams({
      product_id: productId,
      limit: String(limit),
      intent: 'related',
    });
    const resp = await fetch(`/recommendations/products.json?${params}`, {
      credentials: 'same-origin',
    });
    if (!resp.ok) throw new Error(`shopify reco ${resp.status}`);
    const data = await resp.json();
    const cartProductIds = (cart.items || []).map(i => String(i.product_id));
    return (data.products || [])
      .filter(p => !cartProductIds.includes(String(p.id)))
      .slice(0, limit)
      .map(p => ({
        product_id: String(p.id),
        handle: p.handle,
        title: p.title,
        price: p.price,
        image: (p.featured_image && p.featured_image.src) || (p.images && p.images[0] && p.images[0].src) || '',
        variant_id: p.variants && p.variants[0] && p.variants[0].id,
        algo: 'shopify',
        reason: '',
      }));
  };

  /* ── Hydrate via /products/{handle}.js ───────────────────────────────────────── */
  const hydrateItem = async (item) => {
    try {
      const resp = await fetch(`/products/${item.handle}.js`, { credentials: 'same-origin' });
      if (!resp.ok) throw new Error('hydrate failed');
      const p = await resp.json();
      return {
        ...item,
        product_id: String(p.id),
        title: p.title,
        price: p.price,
        image: p.featured_image || item.image || '',
        variant_id: p.variants && p.variants[0] && p.variants[0].id,
      };
    } catch {
      return item;
    }
  };

  /* ── Build items HTML ─────────────────────────────────────────────────────────── */
  const buildItemHTML = (item, idx, surface) => {
    const img = item.image
      ? `<img class="cu-reco__item-img lazyloadt4s"
            src="data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=="
            data-src="${item.image}"
            data-sizes="auto"
            alt="${(item.title || '').replace(/"/g, '&quot;')}"
            loading="lazy">`
      : `<div class="cu-reco__item-img" style="background:var(--cu-bg-muted);"></div>`;

    // Use T4S's native AJAX ATC pattern: anchor with data-action-atc + data-variant-id.
    // global.min.js intercepts [data-action-atc] clicks and handles the AJAX add-to-cart,
    // keeping the user inside the drawer. The href is a no-JS fallback only.
    const atcMarkup = item.variant_id
      ? `<a href="/products/${item.handle}"
            class="cu-reco__item-btn t4s-pr-addtocart"
            data-variant-id="${item.variant_id}"
            data-action-atc
            rel="nofollow"
            aria-label="In den Warenkorb: ${(item.title || '').replace(/"/g, '&quot;')}">
           <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
             <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
           </svg>
           Hinzufügen
         </a>`
      : `<a href="/products/${item.handle}" class="cu-reco__item-btn">Ansehen →</a>`;

    return `
      <div class="cu-reco__item"
        data-cu-reco-product-id="${item.product_id}"
        data-cu-reco-position="${idx}"
        data-cu-reco-surface="${surface}"
        data-cu-reco-algo="${item.algo || ''}">
        <a href="/products/${item.handle}" tabindex="-1" aria-hidden="true">${img}</a>
        <div class="cu-reco__item-body">
          <p class="cu-reco__item-title">
            <a href="/products/${item.handle}">${item.title}</a>
          </p>
          <p class="cu-reco__item-price">${money(item.price)}</p>
          ${item.reason ? `<p style="font-size:11px;color:var(--body-color,#888);margin:0;">${item.reason}</p>` : ''}
          ${atcMarkup}
        </div>
      </div>
    `;
  };

  /* ── Render items into a container ───────────────────────────────────────────── */
  const renderItems = (container, items, surface) => {
    if (!container) return;
    container.innerHTML = items.map((item, i) => buildItemHTML(item, i, surface)).join('');

    // Trigger lazysizes if available
    if (window.lazySizes) {
      container.querySelectorAll('.lazyloadt4s').forEach(img => {
        lazySizes.loader.unveil(img);
      });
    }

    // Register telemetry
    if (window.CartTelemetry) {
      window.CartTelemetry.observeRecoItems(container);
    }
  };

  /* ── Main load function ───────────────────────────────────────────────────────── */
  /**
   * @param {Object} opts
   * @param {HTMLElement} opts.container  — element to render items into
   * @param {HTMLElement} [opts.loadingEl] — shown while loading
   * @param {HTMLElement} [opts.wrapperEl] — hidden if no results
   * @param {number} [opts.limit]         — max items (default 4)
   * @param {string} [opts.surface]       — 'drawer' | 'page'
   * @param {string} [opts.fallbackProductId] — Shopify reco fallback seed
   */
  const load = async (opts) => {
    const {
      container,
      loadingEl,
      wrapperEl,
      limit = 4,
      surface = 'cart',
      fallbackProductId,
    } = opts;

    if (!container) return;

    if (loadingEl) loadingEl.style.display = '';

    try {
      const cart = await (window.CartCore ? window.CartCore.getCart() : fetch('/cart.js').then(r => r.json()));
      const cartItems = cart.items || [];

      if (cartItems.length === 0) {
        if (wrapperEl) wrapperEl.style.display = 'none';
        if (loadingEl) loadingEl.style.display = 'none';
        return;
      }

      const fingerprint = makeFingerprint(cart);
      const cached = getCache();

      let items;
      if (isCacheValid(cached, fingerprint)) {
        items = cached.data;
      } else {
        // Try app proxy first
        try {
          items = await fetchFromProxy(cart, limit);
        } catch (e) {
          // Fall back to Shopify recommendations
          const seedId = fallbackProductId || (cartItems[0] && cartItems[0].product_id);
          if (seedId) {
            items = await fetchFromShopify(cart, limit, seedId);
          } else {
            items = [];
          }
        }

        // Always hydrate via /products/{handle}.js to guarantee variant_id is populated
        items = await Promise.all(items.map(item => hydrateItem(item)));

        setCache(fingerprint, items);
      }

      if (loadingEl) loadingEl.style.display = 'none';

      if (items.length === 0) {
        if (wrapperEl) wrapperEl.style.display = 'none';
        return;
      }

      // Update persona chip if present
      const chipEl = wrapperEl && wrapperEl.querySelector('[data-cu-persona-chip]');
      if (chipEl) {
        const p = window.CartCore ? window.CartCore.getPersona() : { label: '' };
        if (p.label) {
          chipEl.textContent = p.label;
          chipEl.style.display = '';
        } else {
          chipEl.style.display = 'none';
        }
      }

      renderItems(container, items, surface);
      if (wrapperEl) wrapperEl.style.display = '';

    } catch (err) {
      console.warn('[CartRecos] Error:', err);
      if (loadingEl) loadingEl.style.display = 'none';
      if (wrapperEl) wrapperEl.style.display = 'none';
    }
  };

  /* ── Auto-reload on cart events ──────────────────────────────────────────────── */
  // Debounce so rapid cart changes (qty stepper) don't hammer the endpoint
  const reloadAll = () => {
    debounce(() => {
      document.querySelectorAll('[data-cu-reco-root]').forEach(root => {
        const container = root.querySelector('[data-cu-reco-container]');
        const loadingEl = root.querySelector('[data-cu-reco-loading]');
        const surface = root.dataset.cuRecoRoot || 'cart';
        const limit = parseInt(root.dataset.cuRecoLimit || '4');
        load({ container, loadingEl, wrapperEl: root, limit, surface });
      });
    }, 400);
  };

  ['cu:cart:updated', 'cart:updated', 'cart:refresh'].forEach(evt => {
    document.addEventListener(evt, reloadAll);
  });

  /* ── Public API ─────────────────────────────────────────────────────────────── */
  window.CartRecommendations = { load, renderItems };
})();
